import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import { Icon as ContentIcon } from '@gitbook/icons';
import {
    Popover,
    PopoverPortalWrapper,
    PortalToggleable,
    type PortalToggleableRef,
    sizes,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, Emoji, type IconDefinition } from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { PageIconPicker } from './PageIconPicker';

export interface PageIconEditableRef {
    open: () => void;
}

/**
 * Icon or emoji for a page.
 */
export const PageIconEditable = React.forwardRef(function PageIconEditable(
    props: {
        analyticsContext: analytics.AnalyticsContextSpaceContent;
        page: hiveCore.RevisionPage;
        readOnly: boolean;
        size?: number;
        className?: string;
    },
    ref: React.Ref<PageIconEditableRef>
): React.ReactElement | null {
    const { analyticsContext, page, size = sizes.ICON_600, readOnly = true, className } = props;
    const portalRef = React.useRef<PortalToggleableRef>(null);
    const hiveBranch = useHiveBranch();

    React.useImperativeHandle(
        ref,
        () => ({
            open: () => {
                portalRef.current?.open();
            },
        }),
        [portalRef]
    );

    const onChange = (update: hiveCore.RevisionPageIconProps) => {
        assertIsDefined(hiveBranch);

        portalRef.current?.close();

        if (update.emoji === page.emoji && update.icon === page.icon) {
            return;
        }

        hiveBranch.change((change) => {
            change.setPageIcon(page.id, update);
        });

        if (update.emoji) {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_pick_page_emoji',
                pageKind: page.type,
                emoji: update.emoji,
            });
        } else if (update.icon) {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_pick_page_icon',
                pageKind: page.type,
                icon: update.icon,
            });
        } else if (page.emoji) {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_remove_page_emoji',
                pageKind: page.type,
            });
        } else {
            analytics.logEvent({
                ...analyticsContext,
                type: 'edit_remove_page_icon',
                pageKind: page.type,
            });
        }
    };

    if (readOnly) {
        return <PageIcon page={page} size={size} className="m-2" />;
    }

    return (
        <div className={clsx('flex', className)}>
            <PortalToggleable
                ref={portalRef}
                wrapper={PopoverPortalWrapper}
                anchor={({ ref, toggle, opened }) => (
                    <Button
                        ref={ref}
                        onPress={() => toggle()}
                        iconOnly
                        size="xlarge"
                        kind="blank"
                        label={page.emoji || page.icon ? 'Edit icon' : 'Add icon'}
                        active={opened}
                        leadingIcon={
                            <PageIcon
                                page={page}
                                size={size}
                                fallback={<Icon.EmojiReaction size={size} />}
                            />
                        }
                    />
                )}
            >
                <Popover kind="wide" preferredAlignment="middle">
                    <PageIconPicker value={page} onChange={onChange} />
                </Popover>
            </PortalToggleable>
        </div>
    );
});

/**
 * Read-only display of the icon or emoji for a page.
 */
export function PageIcon(props: {
    page: hiveCore.RevisionPageIconProps;
    size?: number;
    className?: string;
    fallback?: React.ReactNode;
}) {
    const { page, size, className } = props;
    const { fallback = <Icon.Page size={size} className={clsx('text-secondary', className)} /> } =
        props;

    if (page.icon) {
        return (
            <ContentIcon
                // @ts-ignore - icon is a IconName
                icon={page.icon}
                size={size}
                className={clsx('bg-[--sp-color-text-secondary]', className)}
            />
        );
    }

    if (page.emoji) {
        return <Emoji code={page.emoji} size={size} className={className} />;
    }

    return fallback;
}

/**
 * Get an icon for a page, to be used in a palette.
 */
export function getPalettePageIcon(
    page: api.RevisionPageBase | hiveCore.RevisionPageIconProps,
    fallback = true
): IconDefinition | null {
    if (page.emoji || page.icon) {
        return <PageIcon page={page} size={sizes.ICON_400} />;
    }
    return fallback ? Icon.Page : null;
}
